import type { BankAccount, ContactEntity } from '../../domain/entities/ContactEntity.js';
import type {
  ContactCreateInput,
  ContactUpdateInput,
  ContactsListOptions,
  IContactsRepository,
} from '../../domain/repositories/IContactsRepository.js';
import { ContactsRemoteDataSource } from '../datasources/ContactsRemoteDataSource.js';
import type { BankAccountDto, ContactDto } from '../dtos/ContactDto.js';

function mapBank(dto: BankAccountDto): BankAccount {
  return {
    bank: dto.bank,
    accountNumber: dto.account_number,
    holder: dto.holder,
    primary: dto.primary ?? false,
  };
}

function unmapBank(b: BankAccount): BankAccountDto {
  return {
    bank: b.bank,
    account_number: b.accountNumber,
    holder: b.holder,
    primary: b.primary,
  };
}

function mapDto(dto: ContactDto): ContactEntity {
  return {
    id: dto.id,
    fullName: dto.full_name,
    idNumber: dto.id_number,
    phone: dto.phone ?? null,
    isPhoneVerify: dto.is_phone_verify ?? false,
    otpProvider: dto.otp_provider ?? null,
    otpCode: dto.otp_code ?? null,
    bankAccounts: (dto.bank_accounts ?? []).map(mapBank),
    photos: dto.photos ?? [],
    orgId: dto.org_id,
    projectId: dto.project_id,
    createdBy: dto.created_by,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
  };
}

export class ContactsRepository implements IContactsRepository {
  constructor(private readonly ds: ContactsRemoteDataSource) {}

  async list(options: ContactsListOptions) {
    const { data, total } = await this.ds.list(options);
    return { data: data.map(mapDto), total };
  }

  async getById(id: string) {
    const dto = await this.ds.getById(id);
    return dto ? mapDto(dto) : null;
  }

  async create(input: ContactCreateInput) {
    const dto = await this.ds.create({
      full_name: input.fullName,
      id_number: input.idNumber ?? null,
      phone: input.phone ?? null,
      bank_accounts: (input.bankAccounts ?? []).map(unmapBank),
      photos: input.photos ?? [],
      org_id: input.orgId,
      project_id: input.projectId ?? null,
    });
    return mapDto(dto);
  }

  async update(id: string, input: ContactUpdateInput) {
    const payload: Partial<ContactDto> = {};
    if (input.fullName !== undefined) payload.full_name = input.fullName;
    if (input.idNumber !== undefined) payload.id_number = input.idNumber;
    if (input.phone !== undefined) payload.phone = input.phone;
    if (input.bankAccounts !== undefined) payload.bank_accounts = input.bankAccounts.map(unmapBank);
    if (input.photos !== undefined) payload.photos = input.photos;
    if (input.projectId !== undefined) payload.project_id = input.projectId;
    const dto = await this.ds.update(id, payload);
    return mapDto(dto);
  }

  async delete(id: string) {
    await this.ds.delete(id);
  }
}
